// Milestone Management Models and Interfaces
// Based on API schemas from api.json

// Enums

export enum MilestoneType {
  ClassCount = 'ClassCount',
  Distance = 'Distance',
  AppointmentCount = 'AppointmentCount',
  TotalSessionCount = 'TotalSessionCount'
}

export enum MilestoneComparisonType {
  GreaterThanOrEqual = 'GreaterThanOrEqual',
  Equal = 'Equal'
}

export enum MilestoneUnit {
  Classes = 'Classes',
  Kilometers = 'Kilometers',
  Miles = 'Miles',
  Days = 'Days',
  Appointments = 'Appointments',
  Sessions = 'Sessions'
}

export enum MilestonePeriodType {
  Lifetime = 'Lifetime',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Custom = 'Custom'
}

export enum MilestoneRewardType {
  Badge = 'Badge',
  Points = 'Points',
  Discount = 'Discount'
}

// Request DTOs

export interface CreateMilestoneConfigRequest {
  name?: string;
  tierName?: string;
  milestoneType: MilestoneType;
  comparisonType: MilestoneComparisonType;
  targetValue: number;
  unit: MilestoneUnit;
  periodType: MilestonePeriodType;
  rewardType: MilestoneRewardType;
  classTypeNames?: string[];
  appointmentTypeNames?: string[];
  startDate?: string; // ISO 8601 date-time format
  weekStartDay?: number;
  monthStartDay?: number;
  customPeriodStart?: string; // ISO 8601 date-time format
  customPeriodEnd?: string; // ISO 8601 date-time format
}

export interface UpdateMilestoneConfigRequest {
  name?: string;
  tierName?: string;
  milestoneType: MilestoneType;
  comparisonType: MilestoneComparisonType;
  targetValue: number;
  unit: MilestoneUnit;
  periodType: MilestonePeriodType;
  rewardType: MilestoneRewardType;
  classTypeNames?: string[];
  appointmentTypeNames?: string[];
  startDate?: string; // ISO 8601 date-time format
  weekStartDay?: number;
  monthStartDay?: number;
  customPeriodStart?: string; // ISO 8601 date-time format
  customPeriodEnd?: string; // ISO 8601 date-time format
}

// Response DTOs

export interface GetMilestoneConfigResponse {
  id: number;
  companyId: number;
  name?: string;
  tierName?: string;
  milestoneType?: string;
  comparisonType?: string;
  targetValue: number;
  unit?: string;
  periodType?: string;
  rewardType?: string;
  isActive: boolean;
  classTypeNames?: string[];
  appointmentTypeNames?: string[];
  startDate?: string;
  weekStartDay?: number;
  monthStartDay?: number;
  customPeriodStart?: string;
  customPeriodEnd?: string;
  createdBy: number;
  createdDate: string;
  updatedBy?: number;
  updatedDate?: string;
}

export interface ListMilestoneConfigResponse {
  id: number;
  companyId: number;
  name?: string;
  tierName?: string;
  milestoneType?: string;
  comparisonType?: string;
  targetValue: number;
  unit?: string;
  periodType?: string;
  rewardType?: string;
  isActive: boolean;
  classTypeNames?: string[];
  appointmentTypeNames?: string[];
  startDate?: string;
  weekStartDay?: number;
  monthStartDay?: number;
  customPeriodStart?: string;
  customPeriodEnd?: string;
  createdBy: number;
  createdDate: string;
  updatedBy?: number;
  updatedDate?: string;
}

export interface PagedMilestoneConfigResponse {
  items: GetMilestoneConfigResponse[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

// List Parameters

export interface ListMilestoneConfigParams {
  pageNumber?: number;
  pageSize?: number;
  isActive?: boolean;
}

// Utility interfaces for DataGrid

export interface MilestoneConfigForDisplay {
  id: number;
  name: string;
  tierName?: string;
  type: string;
  target: string;
  period: string;
  reward: string;
  isActive: boolean;
  createdDate: string;
}

// Client Milestone Models (B2B Staff View)

export interface ClientMilestoneProgressItem {
  milestoneConfigId: number;
  name?: string;
  tierName?: string;
  milestoneType?: string;
  targetValue: number;
  unit?: string;
  periodType?: string;
  rewardType?: string;
  currentValue: number;
  isAchieved: boolean;
  achievedDate?: string;
  achievementId?: number;
}

export interface ListClientMilestoneProgressResponse {
  clientId: number;
  milestones: ClientMilestoneProgressItem[];
}

export interface ClientMilestoneAchievementResponse {
  id: number;
  milestoneConfigId: number;
  milestoneName?: string;
  tierName?: string;
  rewardType?: string;
  achievedValue: number;
  achievedDate: string;
  isNotified: boolean;
  createdDate: string;
}

export interface ClientMilestoneAchievementResponsePagedResults {
  items: ClientMilestoneAchievementResponse[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface CreateClientMilestoneAchievementRequest {
  milestoneConfigId: number;
  achievedValue: number;
  achievedDate: string;
}

export interface ForceRecalculateRequest {
  clientIds?: number[];
}

export interface ForceRecalculateResponse {
  clientsProcessed: number;
  message?: string;
}

// Client Milestone Display Models

export interface ClientMilestoneCardData {
  milestoneConfigId: number;
  name: string;
  tierName?: string;
  description: string;
  milestoneType: string;
  targetValue: number;
  currentValue: number;
  unit?: string;
  periodType?: string;
  rewardType: string;
  rewardIcon: string;
  progressPercentage: number;
  status: 'achieved' | 'in-progress' | 'not-started';
  statusLabel: string;
  achievedDate?: string;
  achievementId?: number;
  isLocked: boolean;
}

export interface ClientMilestoneSummary {
  achieved: number;
  inProgress: number;
  notStarted: number;
  total: number;
}
